import FeatherIcon from "feather-icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Modal,
  Col,
  Row,
  Spinner,
  Input,
} from "reactstrap";
import api from "../helpers/api";
import { formatCreatedDate } from "../constants/utils";
import PermissionGuard from "../components/Common/PermissionGuard";
import ContractCategoryModal from "../components/ContractCategoryModal";
import usePermission from "../Hooks/usePermission";
import { useTranslation } from "react-i18next";

const ContractCategories = () => {
  const { t } = useTranslation();

  const currentUser = JSON.parse(localStorage.getItem("authUser"));
  const organizationId = currentUser?.activeOrganizationId ?? null;
  const { hasPermission } = usePermission();

  const [categoryModal, setCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [detailsModal, setDetailsModal] = useState(false);
  const [selectedDetailsCategory, setSelectedDetailsCategory] = useState(null);

  const toggleDetailsModal = (cat = null) => {
    setDetailsModal(!detailsModal);
    setSelectedDetailsCategory(cat);
  };

  const toggleCategoryModal = (category = null) => {
    setCategoryModal(!categoryModal);
    setSelectedCategory(category || {});
    setErrors({});
    setApiError("");
  };

  const handleCategoryModalClose = () => {
    setCategoryModal(false);
    setSelectedCategory(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setApiError("");
    setErrors({});

    const newErrors = {};
    if (!selectedCategory?.name?.trim()) {
      newErrors.name = t("contract_categories.name_required", "Category name is required");
    } else if (selectedCategory.name.length > 100) {
      newErrors.name = t("contract_categories.name_max_length", "Name must be 100 characters or less");
    }
    if (selectedCategory?.description && selectedCategory.description.length > 500) {
      newErrors.description = t("shared.description_max_length", "Description must be 500 characters or less");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const payload = {
        name: selectedCategory.name.trim(),
        description: selectedCategory.description?.trim() || "",
        organizationId,
      };

      if (selectedCategory?.id) {
        await api.put("organization-contract-category/update", { id: selectedCategory.id, ...payload });
      } else {
        await api.post("organization-contract-category/create", payload);
      }

      handleCategoryModalClose();
      reload(searchTerm);
    } catch (error) {
      setApiError(error?.response?.data?.message || t("shared.error_submission", "An error occurred during submission"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setApiError("");
    try {
      if (selectedCategory?.id) {
        await api.delete(`organization-contract-category/delete/${selectedCategory.id}`);
        reload(searchTerm);
        setDeleteModal(false);
      }
    } catch (error) {
      const status = error?.response?.status;
      const responseError = error?.response?.data?.error;
      const usage = responseError?.contractCount;
      if (status === 409 && typeof usage === "number") {
        setApiError(
          t("contract_categories.cannot_delete_in_use", {
            count: usage,
            defaultValue: "Cannot delete — {{count}} contracts use this category. Reassign them first.",
          })
        );
      } else {
        setApiError(
          error?.response?.data?.message ||
            t("contract_categories.failed_to_delete", "Failed to delete category")
        );
      }
    }
  };

  const handleSearch = (e) => setSearchTerm(e.target.value);

  const reload = useCallback(
    async (search = "") => {
      if (!organizationId) return;
      setLoading(true);
      try {
        const params = new URLSearchParams({ organizationId });
        if (search) params.set("search", search);
        const { data } = await api.get(`organization-contract-category/get-all?${params.toString()}`);
        setCategories(data.response || []);
      } catch (error) {
        setApiError(t("contract_categories.failed_to_load", "Failed to load contract categories"));
      } finally {
        setLoading(false);
      }
    },
    [organizationId, t]
  );

  useEffect(() => {
    reload(searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => reload(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm, reload]);

  return (
    <div className="mt-4">
      <div className="mb-4 d-flex align-items-center gap-3">
        <motion.div
          className="mb-4 d-flex align-items-center gap-3"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <PermissionGuard permission="maintenance:create">
            <button
              type="button"
              className="btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2 transition-all hover-shake"
              onClick={() => toggleCategoryModal()}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" height={21} width={21}>
                <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
              </svg>
              {t("contract_categories.add_category", "Add Contract Category")}
            </button>
          </PermissionGuard>
          <div className="ms-auto" style={{ minWidth: "400px", flexGrow: 1, maxWidth: "500px" }}>
            <Input
              type="text"
              placeholder={t("contract_categories.search_placeholder", "Search contract categories...")}
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </motion.div>
      </div>

      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="card-title text-success">{t("contract_categories.title", "Contract Categories")}</h3>
        </CardHeader>
        <CardBody>
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t("shared.name", "Name")}</th>
                  <th>{t("shared.created_date", "Created Date")}</th>
                  <th>{t("shared.edit", "Edit")}</th>
                  <th>{t("shared.delete", "Delete")}</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {categories?.map((cat, ind) => (
                    <motion.tr
                      key={cat.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <th scope="row">{ind + 1}</th>
                      <td>
                        <span
                          className="text-primary cursor-pointer hover-underline"
                          onClick={() => toggleDetailsModal(cat)}
                          style={{ cursor: "pointer" }}
                        >
                          {cat.name}
                        </span>
                      </td>
                      <td>{formatCreatedDate(cat.createdAt) || "-"}</td>
                      <td>
                        <PermissionGuard permission="maintenance:update">
                          <span className="text-warning" role="button" onClick={() => toggleCategoryModal(cat)}>
                            <FeatherIcon icon="edit" />
                          </span>
                        </PermissionGuard>
                      </td>
                      <td>
                        <PermissionGuard permission="maintenance:delete">
                          <span
                            className="text-danger"
                            role="button"
                            onClick={() => {
                              setSelectedCategory(cat);
                              setApiError("");
                              setDeleteModal(true);
                            }}
                          >
                            <FeatherIcon icon="trash-2" />
                          </span>
                        </PermissionGuard>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {loading && (
            <div className="text-center py-3">
              <Spinner color="primary" />
            </div>
          )}

          {categories.length === 0 && !loading && (
            <div className="text-center py-5 text-muted">
              {searchTerm
                ? t("contract_categories.no_match", "No contract categories match your search")
                : t("contract_categories.empty", "No contract categories found")}
            </div>
          )}
        </CardBody>
      </Card>

      <Modal size="lg" isOpen={categoryModal} toggle={() => toggleCategoryModal()}>
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="modal-header">
            <h5 className="modal-title">
              {selectedCategory?.id
                ? t("contract_categories.edit_category", "Edit Contract Category")
                : t("contract_categories.add_category", "Add Contract Category")}
            </h5>
            <button onClick={handleCategoryModalClose} type="button" className="btn-close" aria-label={t("shared.close", "Close")}></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <Row className="mb-4">
                <label className="col-sm-3 col-form-label">
                  {t("shared.name", "Name")} <span className="text-danger">*</span>
                </label>
                <Col sm={9}>
                  <input
                    type="text"
                    value={selectedCategory?.name || ""}
                    onChange={(e) => setSelectedCategory({ ...selectedCategory, name: e.target.value })}
                    className={`form-control ${errors.name ? "is-invalid" : ""}`}
                    placeholder={t("contract_categories.name_placeholder", "Enter category name")}
                    maxLength={100}
                  />
                  {errors.name && <small className="text-danger">{errors.name}</small>}
                </Col>
              </Row>

              <Row className="mb-4">
                <label className="col-sm-3 col-form-label">{t("shared.description", "Description")}</label>
                <Col sm={9}>
                  <textarea
                    value={selectedCategory?.description || ""}
                    onChange={(e) => setSelectedCategory({ ...selectedCategory, description: e.target.value })}
                    className={`form-control ${errors.description ? "is-invalid" : ""}`}
                    placeholder={t("contract_categories.description_placeholder", "Enter category description")}
                    rows={3}
                    maxLength={500}
                  />
                  {errors.description && <small className="text-danger">{errors.description}</small>}
                  <small className="text-muted">
                    {selectedCategory?.description?.length || 0}/500 {t("shared.characters", "characters")}
                  </small>
                </Col>
              </Row>

              <div className="mt-4">
                {apiError && <div className="alert alert-danger">{apiError}</div>}
                <button
                  type="submit"
                  className="btn btn-success w-lg transition-all hover-grow pulse-on-hover"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? t("shared.processing", "Processing...")
                    : selectedCategory?.id
                    ? t("shared.update", "Update")
                    : t("shared.add", "Add")}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </Modal>

      <ContractCategoryModal
        isOpen={detailsModal}
        toggle={() => toggleDetailsModal()}
        categoryData={selectedDetailsCategory}
        loading={false}
        onEditCategory={hasPermission("maintenance:update") ? (cat) => {
          toggleDetailsModal();
          toggleCategoryModal(cat);
        } : null}
      />

      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <div className="modal-header">
          <h5 className="modal-title">{t("shared.confirm_delete", "Confirm Delete")}</h5>
          <button type="button" className="btn-close" onClick={() => setDeleteModal(false)} aria-label={t("shared.close", "Close")}></button>
        </div>
        <div className="modal-body">
          <p>
            {t("contract_categories.delete_confirmation", {
              name: selectedCategory?.name,
              defaultValue: 'Are you sure you want to delete the contract category "{{name}}"? This action cannot be undone.',
            })}
          </p>
          {apiError && <div className="alert alert-danger">{apiError}</div>}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setDeleteModal(false)}>
            {t("shared.cancel", "Cancel")}
          </button>
          <button type="button" className="btn btn-danger" onClick={handleDelete}>
            {t("shared.delete", "Delete")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ContractCategories;
